import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { IngredientCategory } from '../../types/ingredients';
import { StandardUnit } from '../../types/units';
import { getCategoryLabel, getUnitLabel } from '../../utils/translationHelpers';

interface CreateIngredientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    name: string;
    category: IngredientCategory;
    preferredUnit: StandardUnit;
  }) => void;
  extractedName: string;
  extractedUnit?: string;
}

const categories: IngredientCategory[] = [
  'produce',
  'meat',
  'seafood',
  'deli',
  'dairy',
  'sweetGrocery',
  'savoryGrocery',
  'bakery',
  'frozen',
  'beverages',
  'condiments',
  'other'
];

const units: StandardUnit[] = [
  'piece',
  'gram',
  'kilogram',
  'milliliter',
  'centiliter',
  'deciliter',
  'liter',
  'teaspoon',
  'tablespoon',
  'cup'
];

export const CreateIngredientModal: React.FC<CreateIngredientModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  extractedName,
  extractedUnit
}) => {
  const [name, setName] = useState(extractedName);
  const [category, setCategory] = useState<IngredientCategory>('other');
  const [preferredUnit, setPreferredUnit] = useState<StandardUnit>(
    (extractedUnit as StandardUnit) || 'gram'
  );

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onConfirm({ name: name.trim(), category, preferredUnit });
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Icon */}
          <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
            <Plus className="h-6 w-6 text-green-600" />
          </div>

          {/* Content */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Créer un nouvel ingrédient
          </h3>
          
          <p className="text-sm text-gray-600 mb-4">
            Aucun ingrédient correspondant trouvé. Créez-en un nouveau pour enrichir la base de données.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'ingrédient <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: Tomates concassées"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Catégorie <span className="text-red-500">*</span>
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as IngredientCategory)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {getCategoryLabel(cat)}
                  </option>
                ))}
              </select>
            </div>

            {/* Preferred Unit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unité préférée <span className="text-red-500">*</span>
              </label>
              <select
                value={preferredUnit}
                onChange={(e) => setPreferredUnit(e.target.value as StandardUnit)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {units.map((unit) => (
                  <option key={unit} value={unit}>
                    {getUnitLabel(unit)}
                  </option>
                ))}
              </select>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800">
                ⚠️ L'ingrédient sera créé avec des attributs par défaut. 
                Vous pourrez le compléter plus tard depuis la gestion des ingrédients.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                Créer l'ingrédient
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

